// Birds the learner has decided not to learn.
//
// A pack is a region, not a syllabus. It carries every species eBird says you
// could plausibly meet there, which is the right thing for it to carry and the
// wrong thing to be quizzed on without appeal: a gull complex you will never
// separate, a rarity you saw once, forty ducks when you came here for
// passerines. Before this the only ways out were to answer them wrong forever
// or to study a smaller pack, and neither is "I am not learning that one."
//
// Ignoring is deliberately not deleting. The card, the review log and whatever
// mastery was already earned all stay exactly as they are, because the learner
// is saying something about the future — don't ask me this — and un-ignoring has
// to give the bird back unharmed. All it changes is which pool the quizzes draw
// from, and it changes that everywhere at once by being the one place the pools
// come from.
//
// It is stored with progress rather than in localStorage (which is where the
// display preferences live) for the same reason: it is a fact about the learner,
// not about the device, so it belongs to the account and travels with it.

import { getSetting, setSetting } from './db';
import { speciesCodes, speciesList } from './region';
import type { Species } from '../types';

const KEY = 'ignored';

/**
 * The working copy, so every render path can ask synchronously.
 *
 * Same shape of decision as core/db.ts's snapshot: the Library asks "is this one
 * ignored" once per row for a hundred and sixty rows while building a list, and
 * a promise per row would mean the list paints and then re-paints. Loaded once
 * at boot by `loadIgnored()`; every write updates it before it touches storage.
 */
let ignored = new Set<string>();

/**
 * Read the set into memory. Called once during boot, after storage is up and
 * before the first view renders.
 *
 * Tolerant of anything in the slot: this is a settings value, and a settings map
 * survives app versions, imports and hand-edited JSON backups. A malformed one
 * means nothing is ignored, which is the state the app shipped in.
 */
export async function loadIgnored(): Promise<void> {
  const raw = await getSetting<unknown>(KEY);
  ignored = new Set(
    Array.isArray(raw) ? raw.filter((c): c is string => typeof c === 'string' && c !== '') : [],
  );
}

/** Whether this species has been set aside. */
export function isIgnored(code: string): boolean {
  return ignored.has(code);
}

/** How many species are set aside, across every pack — not just the active one. */
export function ignoredCount(): number {
  return ignored.size;
}

/** Every ignored code, for export and for tests. Order is not meaningful. */
export function ignoredCodes(): string[] {
  return [...ignored];
}

/**
 * Set aside a bird, or take it back.
 *
 * Memory first, storage after: the Library re-renders the row from `isIgnored`
 * on the same tick as the tap, and the write is the ordinary background one
 * every other progress write is. The returned promise is for tests and for
 * anywhere that genuinely needs the write to have landed.
 */
export function setIgnored(code: string, on: boolean): Promise<void> {
  if (on) ignored.add(code);
  else ignored.delete(code);
  return setSetting(KEY, [...ignored]);
}

/** Toggle, returning the state the species ended up in. */
export function toggleIgnored(code: string): boolean {
  const next = !isIgnored(code);
  void setIgnored(code, next);
  return next;
}

/**
 * The species a quiz may draw on: the active packs, minus what's been set aside.
 *
 * Every pool in core/quiz/** is built from this and `studyCodes()` rather than
 * from `speciesList()`/`speciesCodes()` directly, which is what makes one
 * setting reach five quiz modes, the distractor lists and the due counts without
 * any of them knowing this module exists. The Library and the species page keep
 * using the region's own list, because an ignored bird has to stay visible — it
 * is the only place the decision can be undone.
 */
export function studyList(): Species[] {
  if (!ignored.size) return speciesList();
  return speciesList().filter((sp) => !ignored.has(sp.code));
}

/** `studyList()` as codes. */
export function studyCodes(): string[] {
  if (!ignored.size) return speciesCodes();
  return speciesCodes().filter((c) => !ignored.has(c));
}

/** Drop the ignored species from an arbitrary list of codes. */
export function withoutIgnored(codes: string[]): string[] {
  if (!ignored.size) return codes;
  return codes.filter((c) => !ignored.has(c));
}

/** Test seam: forget everything this module has loaded. */
export function resetIgnoredForTests(): void {
  ignored = new Set();
}
